import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useAuth } from "../lib/auth-context.js";
import { ApiError } from "../lib/api.js";
import {
  createTradeEntry,
  deleteTradeEntry,
  listTradeEntries,
  updateTradeEntry,
} from "../lib/api-modules/trade-journal.js";
import type {
  CreateTradeEntryInput,
  MembershipRole,
  TradeEntry,
  TradeSide,
} from "../lib/api-modules/types.js";
import {
  centsToDollars,
  dollarsToCents,
  formatCentsCurrency,
} from "../lib/util/money.js";
import { ErrorMessage } from "../components/auth/error-message.js";
import { ConfirmButton } from "../components/confirm-button.js";
import { Toast, useToast } from "../components/toast.js";
import { SuggestedPromptsTile } from "../components/SuggestedPromptsTile.js";
import { PAGE_PROMPTS } from "../lib/data/page-prompts.js";

/**
 * /journal — household-scoped CRUD for trade-journal entries. Mirrors
 * the /watchlists page structure: read access for every member; write
 * access for owner/admin/member/contributor; viewer + auditor see the
 * list with no write controls.
 *
 * Open vs closed: an entry is "open" until both `exit_date` and
 * `exit_price_cents` are set. Closing happens via the same PATCH
 * endpoint via the Close-out button.
 *
 * All `*_price_cents` fields are integer cents on the wire; the form
 * displays dollars and converts at the edge via `dollarsToCents` /
 * `centsToDollars`.
 */

const READ_ONLY_ROLES: ReadonlySet<MembershipRole> = new Set([
  "viewer",
  "auditor",
]);

export function JournalPage() {
  const { user } = useAuth();
  if (!user) return null;
  const canWrite = !READ_ONLY_ROLES.has(user.role);

  return (
    <div className="mx-auto max-w-5xl p-6">
      <header className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Trade journal</h1>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
            Thesis, entry, exit, and outcomes. Closed P&amp;L computed at read time.
          </p>
        </div>
      </header>

      {!canWrite && (
        <p className="mb-6 rounded-md border border-zinc-200 bg-zinc-50 p-3 text-sm text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400">
          Read-only access — ask an owner or admin to make changes.
        </p>
      )}

      {canWrite && <CreateEntryForm />}
      <EntriesList canWrite={canWrite} />
      <div className="mt-8">
        <SuggestedPromptsTile prompts={PAGE_PROMPTS.journal} />
      </div>
    </div>
  );
}

function EntriesList({ canWrite }: { canWrite: boolean }) {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ["trade-journal"],
    queryFn: listTradeEntries,
  });

  return (
    <section aria-labelledby="entries-heading" className="mt-6">
      <h2 id="entries-heading" className="mb-3 text-lg font-medium">
        Entries
      </h2>
      {isPending && <p className="text-sm text-zinc-500">Loading…</p>}
      {isError && (
        <ErrorMessage>
          Couldn't load entries:{" "}
          {error instanceof Error ? error.message : "unknown"}
        </ErrorMessage>
      )}
      {data && data.entries.length === 0 && (
        <p className="text-sm text-zinc-500">No entries yet.</p>
      )}
      {data && data.entries.length > 0 && (
        <ul className="space-y-3">
          {data.entries.map((e) => (
            <EntryCard key={e.entry_id} entry={e} canWrite={canWrite} />
          ))}
        </ul>
      )}
    </section>
  );
}

function EntryCard({
  entry,
  canWrite,
}: {
  entry: TradeEntry;
  canWrite: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [closing, setClosing] = useState(false);

  if (editing) {
    return (
      <li className="rounded-md border border-zinc-200 p-4 dark:border-zinc-800">
        <EditEntryForm entry={entry} onDone={() => setEditing(false)} />
      </li>
    );
  }

  if (closing) {
    return (
      <li className="rounded-md border border-zinc-200 p-4 dark:border-zinc-800">
        <CloseEntryForm entry={entry} onDone={() => setClosing(false)} />
      </li>
    );
  }

  const isOpen = entry.exit_price_cents === undefined;
  const pnl = entry.closed_pnl_cents;

  return (
    <li className="rounded-md border border-zinc-200 p-4 dark:border-zinc-800">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/chart/${encodeURIComponent(entry.ticker)}`}
              className="rounded-md bg-zinc-100 px-2 py-0.5 text-sm font-medium text-zinc-700 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700"
            >
              {entry.ticker}
            </Link>
            <span
              className={
                entry.side === "buy"
                  ? "rounded-md bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
                  : "rounded-md bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-950/40 dark:text-red-300"
              }
            >
              {entry.side}
            </span>
            <span
              className={
                isOpen
                  ? "rounded-md bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-950/40 dark:text-blue-300"
                  : "rounded-md bg-zinc-200 px-2 py-0.5 text-xs font-medium text-zinc-700 dark:bg-zinc-700 dark:text-zinc-300"
              }
            >
              {isOpen ? "open" : "closed"}
            </span>
          </div>
          <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1 text-sm md:grid-cols-2">
            <Field label="Entry">
              {entry.entry_date} @ ${centsToDollars(entry.entry_price_cents)} ×{" "}
              {entry.quantity}
            </Field>
            {entry.exit_date && entry.exit_price_cents !== undefined && (
              <Field label="Exit">
                {entry.exit_date} @ ${centsToDollars(entry.exit_price_cents)}
              </Field>
            )}
            {entry.stop_price_cents !== undefined && (
              <Field label="Stop">${centsToDollars(entry.stop_price_cents)}</Field>
            )}
            {entry.target_price_cents !== undefined && (
              <Field label="Target">
                ${centsToDollars(entry.target_price_cents)}
              </Field>
            )}
            {pnl !== null && pnl !== undefined && (
              <Field label="Closed P&L">
                <span
                  className={
                    pnl >= 0
                      ? "font-semibold text-emerald-700 dark:text-emerald-400"
                      : "font-semibold text-red-700 dark:text-red-400"
                  }
                >
                  {formatCentsCurrency(pnl)}
                </span>
              </Field>
            )}
          </dl>
          {entry.thesis && (
            <p className="mt-3 whitespace-pre-wrap text-sm text-zinc-700 dark:text-zinc-300">
              {entry.thesis}
            </p>
          )}
          {entry.outcome_notes && (
            <p className="mt-2 whitespace-pre-wrap text-sm text-zinc-600 dark:text-zinc-400">
              <span className="font-medium">Outcome:</span> {entry.outcome_notes}
            </p>
          )}
        </div>
        {canWrite && (
          <div className="flex shrink-0 flex-col items-end gap-2">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="rounded-md border border-zinc-300 px-2 py-1 text-xs hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
            >
              Edit
            </button>
            {isOpen && (
              <button
                type="button"
                onClick={() => setClosing(true)}
                className="rounded-md border border-zinc-300 px-2 py-1 text-xs hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
              >
                Close out
              </button>
            )}
            <DeleteButton entry={entry} />
          </div>
        )}
      </div>
    </li>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-2">
      <dt className="text-zinc-500">{label}:</dt>
      <dd className="font-mono text-sm">{children}</dd>
    </div>
  );
}

function DeleteButton({ entry }: { entry: TradeEntry }) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => deleteTradeEntry(entry.entry_id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["trade-journal"] });
    },
  });
  return (
    <ConfirmButton
      label="Delete"
      pendingLabel="Deleting…"
      onConfirm={() => mutation.mutate()}
      isPending={mutation.isPending}
    />
  );
}

// === Create form =======================================================

function CreateEntryForm() {
  const queryClient = useQueryClient();
  const [ticker, setTicker] = useState("");
  const [side, setSide] = useState<TradeSide>("buy");
  const [entryDate, setEntryDate] = useState(todayIso());
  const [entryDollars, setEntryDollars] = useState("");
  const [quantity, setQuantity] = useState("");
  const [stopDollars, setStopDollars] = useState("");
  const [targetDollars, setTargetDollars] = useState("");
  const [thesis, setThesis] = useState("");
  const [inlineError, setInlineError] = useState<string | null>(null);
  const toast = useToast();

  const mutation = useMutation({
    mutationFn: () => {
      const input: CreateTradeEntryInput = {
        ticker: ticker.trim().toUpperCase(),
        side,
        entry_date: entryDate,
        entry_price_cents: dollarsToCents(entryDollars),
        quantity: Number(quantity),
        thesis,
      };
      if (stopDollars.trim().length > 0) {
        input.stop_price_cents = dollarsToCents(stopDollars);
      }
      if (targetDollars.trim().length > 0) {
        input.target_price_cents = dollarsToCents(targetDollars);
      }
      return createTradeEntry(input);
    },
    onSuccess: async (createdEntry) => {
      setTicker("");
      setEntryDollars("");
      setQuantity("");
      setStopDollars("");
      setTargetDollars("");
      setThesis("");
      toast.show(`Logged ${createdEntry.ticker} ${createdEntry.side}`);
      await queryClient.invalidateQueries({ queryKey: ["trade-journal"] });
    },
    onError: (err) => {
      setInlineError(
        err instanceof ApiError
          ? `${err.message} (code: ${err.code})`
          : "Couldn't create. Try again.",
      );
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setInlineError(null);
    mutation.mutate();
  }

  const submitDisabled =
    mutation.isPending ||
    ticker.trim().length === 0 ||
    entryDollars.trim().length === 0 ||
    quantity.trim().length === 0 ||
    Number(quantity) <= 0 ||
    thesis.trim().length === 0;

  return (
    <section aria-labelledby="create-heading" className="mt-2">
      <h2 id="create-heading" className="mb-3 text-lg font-medium">
        New entry
      </h2>
      <form
        onSubmit={onSubmit}
        className="flex flex-col gap-3 rounded-lg border border-zinc-200 p-4 dark:border-zinc-800"
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <FormInput
            id="j-ticker"
            label="Ticker"
            type="text"
            value={ticker}
            onChange={setTicker}
            disabled={mutation.isPending}
            required
            uppercase
            placeholder="AAPL"
          />
          <FormSelect
            id="j-side"
            label="Side"
            value={side}
            options={["buy", "sell"]}
            onChange={(v) => setSide(v as TradeSide)}
            disabled={mutation.isPending}
          />
          <FormInput
            id="j-entry-date"
            label="Entry date"
            type="date"
            value={entryDate}
            onChange={setEntryDate}
            disabled={mutation.isPending}
            required
          />
          <FormInput
            id="j-entry-price"
            label="Entry price ($)"
            type="number"
            step="0.01"
            value={entryDollars}
            onChange={setEntryDollars}
            disabled={mutation.isPending}
            required
          />
          <FormInput
            id="j-quantity"
            label="Quantity"
            type="number"
            step="0.0001"
            value={quantity}
            onChange={setQuantity}
            disabled={mutation.isPending}
            required
          />
          <FormInput
            id="j-stop"
            label="Stop ($, optional)"
            type="number"
            step="0.01"
            value={stopDollars}
            onChange={setStopDollars}
            disabled={mutation.isPending}
          />
          <FormInput
            id="j-target"
            label="Target ($, optional)"
            type="number"
            step="0.01"
            value={targetDollars}
            onChange={setTargetDollars}
            disabled={mutation.isPending}
          />
        </div>
        <div>
          <label htmlFor="j-thesis" className="mb-1 block text-sm font-medium">
            Thesis
          </label>
          <textarea
            id="j-thesis"
            required
            rows={3}
            value={thesis}
            onChange={(e) => setThesis(e.target.value)}
            disabled={mutation.isPending}
            className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
            placeholder="Why this trade, what's the setup, what would invalidate it…"
          />
        </div>
        <div>
          <button
            type="submit"
            disabled={submitDisabled}
            className="rounded-md bg-brand-500 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {mutation.isPending ? "Logging…" : "Log entry"}
          </button>
        </div>
      </form>
      {inlineError && (
        <div className="mt-3">
          <ErrorMessage>{inlineError}</ErrorMessage>
        </div>
      )}
      <Toast message={toast.message} />
    </section>
  );
}

// === Edit form =========================================================

function EditEntryForm({
  entry,
  onDone,
}: {
  entry: TradeEntry;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [stopDollars, setStopDollars] = useState(
    entry.stop_price_cents !== undefined
      ? centsToDollars(entry.stop_price_cents)
      : "",
  );
  const [targetDollars, setTargetDollars] = useState(
    entry.target_price_cents !== undefined
      ? centsToDollars(entry.target_price_cents)
      : "",
  );
  const [thesis, setThesis] = useState(entry.thesis);
  const [inlineError, setInlineError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      updateTradeEntry(entry.entry_id, {
        thesis,
        ...(stopDollars.trim().length > 0
          ? { stop_price_cents: dollarsToCents(stopDollars) }
          : {}),
        ...(targetDollars.trim().length > 0
          ? { target_price_cents: dollarsToCents(targetDollars) }
          : {}),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["trade-journal"] });
      onDone();
    },
    onError: (err) => {
      setInlineError(
        err instanceof ApiError
          ? `${err.message} (code: ${err.code})`
          : "Couldn't update. Try again.",
      );
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setInlineError(null);
    mutation.mutate();
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-3">
      <p className="text-sm text-zinc-500">
        Editing {entry.ticker} {entry.side}. Identity fields are immutable.
      </p>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <FormInput
          id={`j-edit-stop-${entry.entry_id}`}
          label="Stop ($)"
          type="number"
          step="0.01"
          value={stopDollars}
          onChange={setStopDollars}
          disabled={mutation.isPending}
        />
        <FormInput
          id={`j-edit-target-${entry.entry_id}`}
          label="Target ($)"
          type="number"
          step="0.01"
          value={targetDollars}
          onChange={setTargetDollars}
          disabled={mutation.isPending}
        />
      </div>
      <div>
        <label
          htmlFor={`j-edit-thesis-${entry.entry_id}`}
          className="mb-1 block text-sm font-medium"
        >
          Thesis
        </label>
        <textarea
          id={`j-edit-thesis-${entry.entry_id}`}
          rows={3}
          value={thesis}
          onChange={(e) => setThesis(e.target.value)}
          disabled={mutation.isPending}
          className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
        />
      </div>
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={mutation.isPending}
          className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          onClick={onDone}
          disabled={mutation.isPending}
          className="rounded-md border border-zinc-300 px-3 py-1.5 text-sm hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
        >
          Cancel
        </button>
      </div>
      {inlineError && <ErrorMessage>{inlineError}</ErrorMessage>}
    </form>
  );
}

// === Close-out form ====================================================

function CloseEntryForm({
  entry,
  onDone,
}: {
  entry: TradeEntry;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [exitDate, setExitDate] = useState(todayIso());
  const [exitDollars, setExitDollars] = useState("");
  const [outcomeNotes, setOutcomeNotes] = useState("");
  const [inlineError, setInlineError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      updateTradeEntry(entry.entry_id, {
        exit_date: exitDate,
        exit_price_cents: dollarsToCents(exitDollars),
        ...(outcomeNotes ? { outcome_notes: outcomeNotes } : {}),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["trade-journal"] });
      onDone();
    },
    onError: (err) => {
      setInlineError(
        err instanceof ApiError
          ? `${err.message} (code: ${err.code})`
          : "Couldn't close. Try again.",
      );
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setInlineError(null);
    mutation.mutate();
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-3">
      <p className="text-sm text-zinc-500">
        Closing {entry.ticker} {entry.side} (entered {entry.entry_date} @ $
        {centsToDollars(entry.entry_price_cents)}).
      </p>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <FormInput
          id={`j-close-date-${entry.entry_id}`}
          label="Exit date"
          type="date"
          value={exitDate}
          onChange={setExitDate}
          disabled={mutation.isPending}
          required
        />
        <FormInput
          id={`j-close-price-${entry.entry_id}`}
          label="Exit price ($)"
          type="number"
          step="0.01"
          value={exitDollars}
          onChange={setExitDollars}
          disabled={mutation.isPending}
          required
        />
      </div>
      <div>
        <label
          htmlFor={`j-close-notes-${entry.entry_id}`}
          className="mb-1 block text-sm font-medium"
        >
          Outcome notes <span className="text-zinc-500">(optional)</span>
        </label>
        <textarea
          id={`j-close-notes-${entry.entry_id}`}
          rows={2}
          value={outcomeNotes}
          onChange={(e) => setOutcomeNotes(e.target.value)}
          disabled={mutation.isPending}
          className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
          placeholder="What happened, what worked, what to remember next time…"
        />
      </div>
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={
            mutation.isPending || exitDollars.trim().length === 0
          }
          className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending ? "Closing…" : "Close out"}
        </button>
        <button
          type="button"
          onClick={onDone}
          disabled={mutation.isPending}
          className="rounded-md border border-zinc-300 px-3 py-1.5 text-sm hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
        >
          Cancel
        </button>
      </div>
      {inlineError && <ErrorMessage>{inlineError}</ErrorMessage>}
    </form>
  );
}

// === Reusable form bits =================================================

function FormInput({
  id,
  label,
  type,
  step,
  value,
  onChange,
  disabled,
  required,
  uppercase,
  placeholder,
}: {
  id: string;
  label: string;
  type: string;
  step?: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  required?: boolean;
  uppercase?: boolean;
  placeholder?: string;
}) {
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-sm font-medium">
        {label}
      </label>
      <input
        id={id}
        type={type}
        step={step}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        className={`w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900 ${uppercase ? "uppercase" : ""}`}
      />
    </div>
  );
}

function FormSelect({
  id,
  label,
  value,
  options,
  onChange,
  disabled,
}: {
  id: string;
  label: string;
  value: string;
  options: readonly string[];
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-sm font-medium">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

function todayIso(): string {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return fmt.format(new Date());
}
